import { registerSW } from 'virtual:pwa-register';
import { toast } from 'sonner';

/**
 * Tells the user when a new version has been deployed.
 *
 * The service worker is registered `autoUpdate`, which installs and activates a
 * new build on its own — but the tab already open keeps running the JavaScript it
 * loaded, and this is a console people leave open all day. So the desk kept
 * seeing bugs that had already been fixed, and reporting them again.
 *
 * Deliberately a prompt rather than an automatic reload: a reload throws away
 * whatever is half-typed in a form, and the desk is usually mid-way through
 * adding somebody. The toast does not auto-dismiss, so it is still there when
 * they finish.
 */
export function watchForUpdates(): void {
  // Nothing to watch in dev, where there is no service worker at all.
  if (import.meta.env.DEV) return;

  const updateSW = registerSW({
    immediate: true,

    onNeedRefresh() {
      toast('A new version of Energise is ready', {
        description: 'Reload to pick up the latest fixes. Anything you are typing will be lost.',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => {
            void updateSW(true);
          },
        },
      });
    },

    onOfflineReady() {
      // Silent on purpose. The staff console is useless offline anyway — every
      // screen reads live data — so announcing it would promise something untrue.
    },

    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // A tab left open for days would otherwise never check. Hourly is often
      // enough to catch a deploy without being traffic worth thinking about.
      setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });
}
